<template>
    <OnboardingErrorView v-if="run.apiError.value" embedded @retry="handleRetry" />

    <IdentityStep
        v-else-if="view === 'identity'"
        embedded
        :prefill="run.run.value.identity_prefill"
        :busy="run.busy.value"
        :initial-manages-people="run.run.value.manages_people"
        returning
        @submit="handleIdentity" />

    <IntroStep v-else-if="view === 'intro'" embedded :first-name="run.firstName.value" :manages-people="run.managesPeople.value" @complete="showNextScreen()" />

    <ContextRingsStep v-else-if="view === 'context_rings'" embedded :context="setup.context.value" @continue="showNextScreen()" />

    <CalendarConnectStep
        v-else-if="view === 'calendar_connect'"
        embedded
        :calendar="run.run.value.calendar"
        :organization-name="run.run.value.organization_name"
        :busy="run.busy.value"
        @connect="run.connectCalendar"
        @skip="handleSkipCalendar" />

    <ThinkingScreen
        v-else-if="view === 'scanning'"
        embedded
        v-bind="THINKING_SCREENS.scan"
        :label="run.scanLabel.value"
        :show-continue="run.scanPhase.outcome.value === 'timed_out' || run.scanPhase.outcome.value === 'error'"
        @continue="showNextScreen()" />

    <CalendarPayoffStep
        v-else-if="view === 'calendar_payoff'"
        embedded
        :stats="run.scanStats.value"
        :readings="run.scanReadings.value"
        :readings-settled="run.readingsSettled.value"
        @continue="showNextScreen()" />

    <AssessmentIntroStep
        v-else-if="view === 'assessment_intro'"
        embedded
        :stats="run.scanStats.value"
        :manages-people="run.managesPeople.value"
        :question-count="run.run.value.assessment_question_count"
        :calendar-skipped="!calendarConnected"
        @continue="showNextScreen()"
        @skip="handleSkipAssessment" />

    <AssessmentStep
        v-else-if="view === 'assessment'"
        embedded
        :assessment="run.assessment.value"
        :busy="run.busy.value"
        @submitted="handleAssessmentComplete"
        @skip="handleSkipAssessment" />

    <ThinkingScreen v-else-if="view === 'perspective_result' && run.perspectiveResult.value === null" embedded v-bind="THINKING_SCREENS.perspective" />

    <PerspectiveResultStep
        v-else-if="view === 'perspective_result'"
        embedded
        :result="run.perspectiveResult.value!"
        :calendar-connected="calendarConnected"
        :retakeable="startedAtResult"
        @continue="showNextScreen()"
        @retake="handleRetake" />

    <!-- The first reading to land ends the thinking screen; the readings step
         fills the remaining slots in place as the rest arrive. -->
    <ThinkingScreen
        v-else-if="view === 'sharpened' && !run.sharpenedSettled.value && !run.hasSharpenedReadings.value"
        embedded
        v-bind="THINKING_SCREENS.readings"
        :show-continue="run.sharpenedPhase.outcome.value === 'timed_out' || run.sharpenedPhase.outcome.value === 'error'"
        @continue="showNextScreen()" />

    <SharpenedReadingsStep
        v-else-if="view === 'sharpened'"
        embedded
        :readings="run.sharpenedReadings.value"
        :first-pass-readings="run.scanReadings.value"
        :settled="run.sharpenedSettled.value"
        :available-access-levels="run.run.value.available_access_levels"
        :calendar-connected="calendarConnected"
        @continue="showNextScreen()" />

    <ProfileIntroStep
        v-else-if="view === 'profile_intro'"
        embedded
        :first-name="run.run.value.identity_prefill.first_name"
        :manages-people="run.managesPeople.value"
        :busy="run.busy.value"
        @continue="showNextScreen()"
        @skip="showNextScreen()" />

    <ThinkingScreen
        v-else-if="view === 'profile' && run.profileGenerating.value"
        embedded
        v-bind="THINKING_SCREENS.profile"
        :show-continue="run.profilePhase.outcome.value === 'timed_out'"
        @continue="showNextScreen()" />

    <ProfilePublishStep
        v-else-if="view === 'profile'"
        embedded
        :preview="run.profilePreview.value"
        :available-access-levels="run.run.value.available_access_levels"
        :profile-sharer-names="run.scanStats.value?.profile_sharer_names ?? []"
        :generation-failed="run.profilePreview.value.status === 'failed'"
        :busy="run.busy.value"
        @publish="handlePublish"
        @skip="showNextScreen()" />
</template>

<script setup lang="ts">
/**
 * Do not call /advance/ here: it requires the server cursor to match the departing step, which out-of-order
 * setup cannot guarantee. Screen transitions stay local.
 */
import AssessmentIntroStep from "~vue/CalendarFirstOnboarding/AssessmentIntroStep.vue";
import AssessmentStep from "~vue/CalendarFirstOnboarding/AssessmentStep.vue";
import CalendarConnectStep from "~vue/CalendarFirstOnboarding/CalendarConnectStep.vue";
import CalendarPayoffStep from "~vue/CalendarFirstOnboarding/CalendarPayoffStep.vue";
import ContextRingsStep from "~vue/CalendarFirstOnboarding/ContextRingsStep.vue";
import IdentityStep from "~vue/CalendarFirstOnboarding/IdentityStep.vue";
import IntroStep from "~vue/CalendarFirstOnboarding/IntroStep.vue";
import OnboardingErrorView from "~vue/CalendarFirstOnboarding/OnboardingErrorView.vue";
import PerspectiveResultStep from "~vue/CalendarFirstOnboarding/PerspectiveResultStep.vue";
import ProfileIntroStep from "~vue/CalendarFirstOnboarding/ProfileIntroStep.vue";
import ProfilePublishStep from "~vue/CalendarFirstOnboarding/ProfilePublishStep.vue";
import SharpenedReadingsStep from "~vue/CalendarFirstOnboarding/SharpenedReadingsStep.vue";
import ThinkingScreen from "~vue/CalendarFirstOnboarding/ThinkingScreen.vue";
import { THINKING_SCREENS } from "~vue/CalendarFirstOnboarding/thinkingScreens";
import type { AccessLevel } from "~vue/CalendarFirstOnboarding/types";
import type { IdentitySubmission } from "~vue/CalendarFirstOnboarding/useOnboardingRun";
import type { SetupStepId } from "~vue/ProfileSetup/types";
import { type ProfileSetup, screensFor, type SetupScreen } from "~vue/ProfileSetup/useProfileSetup";
import { computed, onBeforeUnmount, ref, watch } from "vue";

const { setup, stepId } = defineProps<{ setup: ProfileSetup; stepId: SetupStepId }>();

const emit = defineEmits<{ scrollReset: [] }>();

const run = setup.run;

/* A connected calendar the user then skipped is not one the readings can lean on. */
const calendarConnected = computed(() => run.run.value.calendar.connected && !run.run.value.calendar_skipped);

const screens = ref<SetupScreen[]>([]);
const view = ref<SetupScreen | null>(null);
let disposed = false;
onBeforeUnmount(() => {
    disposed = true;
});

function isCurrent(): boolean {
    return !disposed && setup.activeStep.value === stepId;
}

async function showScreen(next: SetupScreen | null): Promise<void> {
    if (!isCurrent()) {
        return;
    }
    if (next === null) {
        await continueAfter(setup.activeStep.value);
        return;
    }
    view.value = next;
    emit("scrollReset");
    if ((await run.enterStep(next)) !== "entered") {
        await showNextScreen();
    }
}

async function showNextScreen(): Promise<void> {
    const index = view.value === null ? -1 : screens.value.indexOf(view.value);
    await showScreen(screens.value[index + 1] ?? null);
}

/* A finished ask reopens on what it produced, not on the screen that asks for it again: a connected calendar
   on its scan, an answered assessment on its archetype, a written profile on the draft itself.
   The profile keeps its intro until a draft exists, because that screen's action is what consents to
   generation overwriting whatever the user already wrote. */
function firstScreen(list: SetupScreen[]): SetupScreen | null {
    if (stepId === "calendar" && run.run.value.calendar.connected) {
        return "scanning";
    }
    if (stepId === "perspective" && run.run.value.has_perspective_result) {
        return "perspective_result";
    }
    if (stepId === "publish" && run.run.value.profile_status === "ready") {
        return "profile";
    }
    return list[0] ?? null;
}

/* Only the reopened result screen offers a retake; mid-flow it is the reveal of answers just given. */
const startedAtResult = ref(false);

async function handleRetake(): Promise<void> {
    startedAtResult.value = false;
    await showScreen("assessment_intro");
}

/* Each section owns its runner. Watching the global active section here would enter
   the next ask once before unmounting and again when its own runner mounts. */
watch(
    () => stepId,
    async (id) => {
        screens.value = screensFor(id, { hasPerspectiveResult: run.run.value.has_perspective_result, calendarConnected: calendarConnected.value });
        const first = firstScreen(screens.value);
        startedAtResult.value = first === "perspective_result";
        await showScreen(first);
    },
    { immediate: true },
);

watch(
    () => run.scanPhase.outcome.value,
    async (outcome) => {
        if (view.value === "scanning" && outcome === "resolved" && run.scanPhase.data.value?.status === "stats_ready") {
            await setup.refreshState();
            await showNextScreen();
        }
    },
);

async function continueAfter(ask: SetupStepId | null): Promise<void> {
    if (!isCurrent()) {
        return;
    }
    await setup.refreshState();
    if (!isCurrent()) {
        return;
    }
    const next = setup.nextUnfinished(ask ?? undefined);
    if (next === null) {
        await setup.finish();
        if (isCurrent()) {
            await setup.closeStep();
        }
        return;
    }
    setup.openStep(next);
}

async function handleIdentity(payload: IdentitySubmission): Promise<void> {
    if (!(await run.submitIdentity(payload))) {
        return;
    }
    await setup.refreshState();
    await showNextScreen();
}

/* Stay on this ask if saving the skip fails; otherwise the error's retry would target the next ask. */
async function handleSkipCalendar(): Promise<void> {
    if (!(await run.skipCalendar())) {
        return;
    }
    await continueAfter("calendar");
}

async function handleAssessmentComplete(): Promise<void> {
    if (!(await run.completeAssessment())) {
        return;
    }
    /*
     * Refresh the rail after answers persist so the assessment shows complete while its result screens are
     * still open.
     */
    await setup.refreshState();
    await showNextScreen();
}

async function handleSkipAssessment(): Promise<void> {
    if (!(await run.skipAssessment())) {
        return;
    }
    await continueAfter("perspective");
}

async function handlePublish(access: AccessLevel): Promise<void> {
    if (!(await run.publishProfile(access))) {
        return;
    }
    await continueAfter("publish");
}

async function handleRetry(): Promise<void> {
    run.apiError.value = false;
    if (view.value !== null) {
        await showScreen(view.value);
    }
}
</script>
